import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { CacheManager } from '@/utils/core/cacheManager.js';
import { registerQueueHandler, enqueueOperation, getQueuedPayloads } from '@/sync/offlineSyncQueue';
import { getLatestAppointmentsWithStatements } from '@/api/sales/appointment';
import { getDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getProductDeliveryLocationDiffusions } from '@/api/assort/productDeliveryLocationDiffusion';
import { getProductTrees } from '@/api/core/productTree';
import { buildComparisonRows } from '@/utils/domain/catchmentAreaComparison';

const catchmentAreaCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'CatchmentArea',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.catchmentArea.`,
});

// Cached per reference delivery location, never by a joined array of ids — any store fetched
// through any request stays available offline for every other request that includes it
// (tasks/lessons.md cache-scoping lesson).
function cacheKeyForDeliveryLocation(deliveryLocationId) {
	return `dl.${deliveryLocationId}`;
}

async function apiFetch(endpoint, options = {}) {
	const response = await authenticatedFetch(getApiUrl(endpoint), options, 'tokenSales');
	if (response.status === 404) return null;
	if (!response.ok) throw new Error(`HTTP ${response.status}`);
	if (response.status === 204) return null;
	const text = await response.text();
	return text ? JSON.parse(text) : null;
}

async function fetchCatchmentAreaFromAPI(deliveryLocationId) {
	return apiFetch(`Sales/CatchmentAreas/DeliveryLocation/${deliveryLocationId}`);
}

export async function getCatchmentAreaByDeliveryLocation(deliveryLocationId, forceRefresh = false) {
	const cacheKey = cacheKeyForDeliveryLocation(deliveryLocationId);
	return catchmentAreaCache.fetchOrCache(cacheKey, () => fetchCatchmentAreaFromAPI(deliveryLocationId), { forceRefresh, fallback: null, backgroundRefresh: true });
}

/**
 * Background-sync preload (dataSync.js): every zone in one request, written under the same per-store
 * key getCatchmentAreaByDeliveryLocation reads. Without it a store's zone only reached the cache once
 * its fiche had been opened online, so it showed empty offline until then (#23923). The bulk endpoint
 * returns full zones, not a narrower subset, so sharing the on-demand key is safe here (unlike the
 * windowed diffusions preload). A zone deleted server-side clears its stale cached copy; a store with a
 * queued offline edit keeps its optimistic entry until the queue replays.
 */
export async function preloadAllCatchmentAreas(deliveryLocationIds = []) {
	if (!iframeDataBridge.isOnline()) return;
	if (!Array.isArray(deliveryLocationIds) || deliveryLocationIds.length === 0) return;

	const [zones, queuedDeltas] = await Promise.all([apiFetch('Sales/CatchmentAreas'), getQueuedPayloads('CATCHMENT_AREA_MEMBER_DELTA')]);
	const zonesByDeliveryLocationId = new Map((Array.isArray(zones) ? zones : []).map((zone) => [zone.deliveryLocationId, zone]));
	const pendingIds = new Set(queuedDeltas.map((payload) => payload?.referenceDeliveryLocationId));

	const keys = deliveryLocationIds.map(cacheKeyForDeliveryLocation);
	const cachedByKey = await catchmentAreaCache.getMany(keys);
	for (let i = 0; i < deliveryLocationIds.length; i++) {
		if (pendingIds.has(deliveryLocationIds[i])) continue;
		const zone = zonesByDeliveryLocationId.get(deliveryLocationIds[i]) ?? null;
		if (zone == null && cachedByKey.get(keys[i]) == null) continue;
		await catchmentAreaCache.put(keys[i], zone);
		catchmentAreaCache.setInMemory(keys[i], zone);
	}
}

export async function refreshCatchmentArea(deliveryLocationId) {
	const cacheKey = cacheKeyForDeliveryLocation(deliveryLocationId);
	catchmentAreaCache.deleteFromMemory(cacheKey);
	await catchmentAreaCache.delete(cacheKey);
}

/**
 * Counts only the zone's members that resolve to a delivery location the user is actually synced
 * with — a member id can outlive its delivery location (deleted, or moved out of the user's
 * perimeter) while the zone API still returns it. Matches CatchmentAreaDefinition.vue's own
 * comparedStores filtering, so the relevé's "Comparer" button (gated on this count being > 0,
 * see iframeWatcher.js's probeCatchmentArea) never disagrees with what the fiche shows (#23601).
 */
export async function getResolvedCatchmentAreaMemberCount(deliveryLocationId) {
	const [zone, deliveryLocations] = await Promise.all([getCatchmentAreaByDeliveryLocation(deliveryLocationId), getDeliveryLocations().catch(() => [])]);
	const memberIds = Array.isArray(zone?.members) ? zone.members : [];
	if (!memberIds.length) return 0;
	const validIds = new Set((deliveryLocations || []).map((dl) => dl.id));
	return memberIds.filter((id) => validIds.has(id)).length;
}

function dispatchCatchmentAreaUpdated(deliveryLocationId) {
	window.dispatchEvent(new CustomEvent('catchment-area-updated', { detail: { deliveryLocationId } }));
}

// `members` is a plain array of delivery location ids (confirmed against the real backend schema
// — POST /Sales/CatchmentAreas expects `members: [int]`, not a list of {deliveryLocationId} objects).
function withMemberDelta(members, memberDeliveryLocationId, action) {
	const current = Array.isArray(members) ? members : [];
	if (action === 'add') {
		return current.includes(memberDeliveryLocationId) ? current : [...current, memberDeliveryLocationId];
	}
	return current.filter((id) => id !== memberDeliveryLocationId);
}

// Reads the CURRENT server (or cache, if offline) state, applies one add/remove delta, and
// saves — never replays a full-object snapshot captured earlier, so a concurrent server-side
// change (e.g. a perimeter-cleanup batch job) isn't clobbered by a stale local copy (plan D3).
async function saveOrDeleteMember(referenceDeliveryLocationId, memberDeliveryLocationId, action) {
	const current = (await getCatchmentAreaByDeliveryLocation(referenceDeliveryLocationId, true)) || { deliveryLocationId: referenceDeliveryLocationId, members: [] };
	const nextMembers = withMemberDelta(current.members, memberDeliveryLocationId, action);
	const cacheKey = cacheKeyForDeliveryLocation(referenceDeliveryLocationId);

	// Spec Q-06: a zone with zero members is deleted entirely rather than saved empty.
	if (nextMembers.length === 0) {
		if (current.id != null) {
			await apiFetch(`Sales/CatchmentAreas/${current.id}`, { method: 'DELETE' });
		}
		await catchmentAreaCache.put(cacheKey, null);
		catchmentAreaCache.setInMemory(cacheKey, null);
		return null;
	}

	const saved = await apiFetch('Sales/CatchmentAreas', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ ...current, deliveryLocationId: referenceDeliveryLocationId, members: nextMembers }),
	});
	await catchmentAreaCache.put(cacheKey, saved);
	catchmentAreaCache.setInMemory(cacheKey, saved);
	return saved;
}

async function applyMemberDelta(referenceDeliveryLocationId, memberDeliveryLocationId, action) {
	if (!iframeDataBridge.isOnline()) {
		const cacheKey = cacheKeyForDeliveryLocation(referenceDeliveryLocationId);
		const current = (await getCatchmentAreaByDeliveryLocation(referenceDeliveryLocationId)) || { deliveryLocationId: referenceDeliveryLocationId, members: [] };
		const optimisticMembers = withMemberDelta(current.members, memberDeliveryLocationId, action);
		const optimisticZone = optimisticMembers.length ? { ...current, deliveryLocationId: referenceDeliveryLocationId, members: optimisticMembers } : null;

		await catchmentAreaCache.put(cacheKey, optimisticZone);
		catchmentAreaCache.setInMemory(cacheKey, optimisticZone);
		await enqueueOperation('CATCHMENT_AREA_MEMBER_DELTA', { referenceDeliveryLocationId, memberDeliveryLocationId, action }, `Zone de chalandise – ${action === 'add' ? 'ajout' : 'retrait'} magasin ${memberDeliveryLocationId}`);

		dispatchCatchmentAreaUpdated(referenceDeliveryLocationId);
		return optimisticZone;
	}

	const saved = await saveOrDeleteMember(referenceDeliveryLocationId, memberDeliveryLocationId, action);
	dispatchCatchmentAreaUpdated(referenceDeliveryLocationId);
	return saved;
}

/** Adds a comparable store to a reference store's catchment area (auto-save, no explicit save step). */
export async function addCatchmentAreaMember(referenceDeliveryLocationId, memberDeliveryLocationId) {
	return applyMemberDelta(referenceDeliveryLocationId, memberDeliveryLocationId, 'add');
}

/** Removes a comparable store; deletes the whole zone if it was the last member (spec Q-06). */
export async function removeCatchmentAreaMember(referenceDeliveryLocationId, memberDeliveryLocationId) {
	return applyMemberDelta(referenceDeliveryLocationId, memberDeliveryLocationId, 'remove');
}

// Replay is automatic on SYNC_COMPLETED (offlineSyncQueue.js) — no extra wiring needed beyond
// registering the handler once.
registerQueueHandler('CATCHMENT_AREA_MEMBER_DELTA', async ({ referenceDeliveryLocationId, memberDeliveryLocationId, action }) => {
	await saveOrDeleteMember(referenceDeliveryLocationId, memberDeliveryLocationId, action);
	dispatchCatchmentAreaUpdated(referenceDeliveryLocationId);
});

// --- Comparison composition (plan decision D2 — always client-side, online and offline alike) ---

// An appointment's statements are one per product-tree category (ABBAYE, CATEGORIE B, ...), each
// carrying its own `products` slice — not a single "the" statement. Taking only `statements[0]`
// silently dropped every product outside whichever category happens to sort first (often empty),
// which is why compared-store État/Facing/Prix could read as missing across an entire category
// while VMH/VMM (a separate diffusions fetch) rendered fine. Flatten across all of them, matching
// the same pattern kpiService.js's _collectKpiComputationData already uses for this appointment shape.
function latestStatementProducts(appointment) {
	const statements = appointment?._statements ?? appointment?.statements ?? null;
	if (Array.isArray(statements)) return statements.flatMap((statement) => (statement?.products ?? []).map((product) => withStatementCategory(product, statement)));
	return appointment?.products ?? [];
}

// The surveyed category lives on the statement (shelfStatementModel.productTreeId/productTreeLabel),
// always at the org's survey depth (e.g. "TH") — the STATEMENT's own id must win whenever it's set,
// never the product's. A statement product's own productTreeId is not just "frequently 0": when set,
// it's the product's ORIGIN category (a deeper catalog leaf, e.g. "Pur jus iPlus 750ml") for products
// picked in cross-category flows (picking / multi-univers cross-picking — see sfaHelper.js's own
// comment on this), which counts in the category it was SURVEYED in, not the one it came from. Falling
// back to the product's own id only when the statement carries none at all.
function withStatementCategory(product, statement) {
	const statementProductTreeId = Number(statement?.productTreeId) > 0 ? statement.productTreeId : null;
	const productTreeId = statementProductTreeId ?? (Number(product?.productTreeId) > 0 ? product.productTreeId : null);
	if (productTreeId == null) return product;
	const productTreeLabel = (statementProductTreeId ? statement?.productTreeLabel : product?.productTreeLabel) || '';
	return { ...product, productTreeId, productTreeLabel };
}

// The org-configured Core/ProductTree depth statements are surveyed at (SFAStatementLevel overriding
// RankLevel) — a catalog product's own productTreeId sits at a deeper, leaf/family level than this, so
// buildComparisonRows needs this depth to walk a diffusion-only product's catalog category up to the
// same granularity a surveyed sibling's category would be at. Mirrors kpiService.js's
// _resolveStatementProductTree: sfaHelper.getPOSProductTrees(true) first since it already applies this
// exact resolution, falling back to reading the options directly off the ecosVM snapshot.
function resolveStatementCategoryDepth() {
	const sfaHelper = iframeDataBridge.getSfaHelper();
	const posTrees = typeof sfaHelper?.getPOSProductTrees === 'function' ? sfaHelper.getPOSProductTrees(true) : [];
	if (Array.isArray(posTrees) && posTrees.length && posTrees[0]?.depth != null) return Number(posTrees[0].depth);

	const options = iframeDataBridge.getEcosVM()?.options || [];
	const findOption = (name, application) => options.find((option) => option?.name === name && option?.application === application);
	const statementLevelOption = findOption('SFAStatementLevel', 'SALFO') || findOption('RankLevel', 'ASSFO');
	const depth = statementLevelOption ? Number(statementLevelOption.value) : NaN;
	return Number.isFinite(depth) ? depth : null;
}

/**
 * Composes the full product×store comparison for a reference store entirely client-side, from
 * data that's already offline-cached elsewhere in the app: the zone's members
 * (getCatchmentAreaByDeliveryLocation), each store's latest relevé state/facing/prix
 * (getLatestAppointmentsWithStatements) and sell-out VMH/VMM (getProductDeliveryLocationDiffusions).
 * Works identically online and offline — there is no separate server aggregation call.
 * @param {number} deliveryLocationId - the reference (visited) store
 * @param {object} [options]
 * @param {Array<object>} [options.localReferenceStatement] - the current in-progress visit's own
 *   statement products for the reference store, when a visit is open (plan decision D7) — used
 *   instead of the last-synced snapshot so the comparison reflects what's being entered right now.
 */
export async function getCatchmentAreaComparison(deliveryLocationId, { localReferenceStatement = null } = {}) {
	const [zone, deliveryLocations] = await Promise.all([getCatchmentAreaByDeliveryLocation(deliveryLocationId), getDeliveryLocations().catch(() => [])]);
	const deliveryLocationsById = new Map((deliveryLocations || []).map((dl) => [dl.id, dl]));

	const memberIds = (Array.isArray(zone?.members) ? zone.members : []).filter((id) => deliveryLocationsById.has(id));
	const allStoreIds = [deliveryLocationId, ...memberIds];

	const idsNeedingLatestStatement = localReferenceStatement ? memberIds : allStoreIds;
	const latestAppointments = idsNeedingLatestStatement.length ? await getLatestAppointmentsWithStatements(idsNeedingLatestStatement, { limit: 1 }) : [];

	const statementsByDeliveryLocationId = {};
	latestAppointments.forEach((appointment) => {
		statementsByDeliveryLocationId[appointment.deliveryLocationId] = latestStatementProducts(appointment);
	});

	const diffusionResults = await Promise.all(allStoreIds.map((id) => getProductDeliveryLocationDiffusions({ deliveryLocationId: id }).catch(() => [])));
	const diffusionsByDeliveryLocationId = {};
	allStoreIds.forEach((id, index) => {
		diffusionsByDeliveryLocationId[id] = diffusionResults[index];
	});

	const [products, productTrees] = await Promise.all([Promise.resolve(iframeDataBridge.getProducts()), getProductTrees().catch(() => [])]);
	const productsById = Object.fromEntries((products || []).map((product) => [product.id, product]));
	const productTreesById = Object.fromEntries((productTrees || []).map((tree) => [tree.id, tree]));

	// Same channel/POS-type reference lists CatchmentAreaDefinition.vue already reads off the ecosVM,
	// reused here so the comparison's store headers can show the same "Circuit · Type de PDV" subtitle.
	const ecosVM = iframeDataBridge.getEcosVM();
	const channelsById = new Map((ecosVM?.channels || []).map((entry) => [entry.id, entry.label || entry.name]));
	const posTypesById = new Map((ecosVM?.posTypes || []).map((entry) => [entry.id, entry.label || entry.name]));

	function toComparisonStore(id, dl) {
		return {
			id,
			label: dl?.companyName || dl?.label || `#${id}`,
			channel: channelsById.get(dl?.channelId) || '',
			posType: posTypesById.get(dl?.POSTypeId) || '',
			accountId: dl?.accountId ?? null,
		};
	}

	const comparedStores = memberIds.map((id) => toComparisonStore(id, deliveryLocationsById.get(id)));
	const referenceStore = toComparisonStore(deliveryLocationId, deliveryLocationsById.get(deliveryLocationId));

	return {
		zone,
		referenceStore,
		comparedStores,
		rows: buildComparisonRows({
			referenceStatementProducts: localReferenceStatement || statementsByDeliveryLocationId[deliveryLocationId] || [],
			referenceDiffusions: diffusionsByDeliveryLocationId[deliveryLocationId] || [],
			comparedStores,
			statementsByDeliveryLocationId,
			diffusionsByDeliveryLocationId,
			productsById,
			productTreesById,
			statementCategoryDepth: resolveStatementCategoryDepth(),
		}),
	};
}
